import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import useAuth from '../../auth/hooks/useAuth';
import { brand } from '../../../config/brand';
import { buildArtifactUrls, type BrandId } from '../../../config/releases';
import { useReleases } from '../hooks/useReleases';
import type { ReleaseEntry } from '../api/downloadsApi';

interface ChannelPanelProps {
  release: ReleaseEntry;
  brandId: BrandId;
  mode: 'download' | 'npm';
  copied: boolean;
  onCopy: () => void;
}

function ChannelPanel({ release, brandId, mode, copied, onCopy }: ChannelPanelProps) {
  const artifacts = useMemo(
    () => buildArtifactUrls(release.version, brandId),
    [release.version, brandId]
  );

  if (mode === 'download') {
    return (
      <Box>
        <Grid container spacing={1}>
          {(['windows', 'mac', 'linux'] as const).map((platformKey) => {
            const platform = artifacts[platformKey];
            return (
              <Grid item xs={4} key={platformKey}>
                <Button
                  fullWidth
                  variant="contained"
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={
                    <Box
                      component="img"
                      src={platform.icon}
                      alt={platformKey}
                      sx={{
                        width: 18,
                        height: 18,
                        filter: 'brightness(0) invert(1)',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    />
                  }
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    py: 1,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  {platformKey === 'windows' ? 'Windows' : platformKey === 'mac' ? 'macOS' : 'Linux'}
                </Button>
              </Grid>
            );
          })}
        </Grid>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8, fontSize: '0.7rem', color: 'white' }}>
          Version {release.version} •
          <Box
            component="a"
            href={artifacts.github.releasePageUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'inherit', ml: 0.5, textDecoration: 'underline' }}
          >
            View release notes
          </Box>
        </Typography>
      </Box>
    );
  }

  // NPM tab
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'rgba(0,0,0,0.3)',
          borderRadius: 1,
          p: 1,
          fontFamily: 'monospace',
        }}
      >
        <Typography
          sx={{
            flexGrow: 1,
            color: 'white',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          $ {artifacts.npm.installCommand}
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy command'}>
          <IconButton size="small" onClick={onCopy} sx={{ color: 'white', ml: 1 }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontSize: '0.7rem', color: 'white' }}>
        Requires Node.js 18+. Run <code>{brand.storagePrefix} web</code> after install.
      </Typography>
    </Box>
  );
}

interface ChannelBlockProps {
  label: string;
  release: ReleaseEntry;
  brandId: BrandId;
  badgeColor: 'success' | 'warning';
}

function ChannelBlock({ label, release, brandId, badgeColor }: ChannelBlockProps) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const artifacts = useMemo(
    () => buildArtifactUrls(release.version, brandId),
    [release.version, brandId]
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(artifacts.npm.installCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      sx={{
        bgcolor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: { xs: '100%', md: 500 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'rgba(0,0,0,0.25)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'white', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Chip
          label={release.channel}
          size="small"
          color={badgeColor}
          sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
        />
        <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.7, color: 'white', fontFamily: 'monospace' }}>
          v{release.version}
        </Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          minHeight: 38,
          bgcolor: 'rgba(0,0,0,0.2)',
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 600,
            minHeight: 38,
            py: 0.5,
            '&.Mui-selected': { color: 'white' },
          },
          '& .MuiTabs-indicator': { bgcolor: 'white' },
        }}
      >
        <Tab label="Download" />
        <Tab label="NPM" />
      </Tabs>
      <Box sx={{ p: 1.5 }}>
        <ChannelPanel
          release={release}
          brandId={brandId}
          mode={tab === 0 ? 'download' : 'npm'}
          copied={copied}
          onCopy={handleCopy}
        />
      </Box>
    </Paper>
  );
}

/**
 * Full Downloads section for the homepage. Reads admin-managed releases list
 * at mount and renders stable + experimental channels (whichever are present)
 * plus — for logged-in users — a secondary CTA to the Remote Sessions page.
 */
const DownloadsSection: React.FC = () => {
  const { loading, stable, experimental } = useReleases();
  const { isAuthenticated } = useAuth();
  const brandId: BrandId = (brand.id as BrandId) || 'autopilot';

  if (loading) {
    return (
      <Paper
        sx={{
          bgcolor: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 2,
          maxWidth: { xs: '100%', md: 500 },
        }}
      >
        <Skeleton variant="rectangular" height={38} sx={{ bgcolor: 'rgba(255,255,255,0.15)', mb: 1 }} />
        <Skeleton variant="rectangular" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
      </Paper>
    );
  }

  if (!stable && !experimental) {
    // Nothing to show — rare, but don't render a broken-looking empty box.
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {stable && (
        <ChannelBlock
          label="Stable release"
          release={stable}
          brandId={brandId}
          badgeColor="success"
        />
      )}
      {experimental && (
        <ChannelBlock
          label="Experimental"
          release={experimental}
          brandId={brandId}
          badgeColor="warning"
        />
      )}

      {isAuthenticated && (
        <Button
          component={RouterLink}
          to="/sessions"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          sx={{
            alignSelf: 'flex-start',
            color: 'white',
            borderColor: 'rgba(255,255,255,0.4)',
            bgcolor: 'rgba(255,255,255,0.05)',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
            },
          }}
        >
          Or launch a Remote Session
        </Button>
      )}
    </Box>
  );
};

export default DownloadsSection;
